import { useMytaverseEvent } from '@core/dashboard/providers';
import { isMapFeatureFlagEnabled } from '@helpers/events';

import { useMapEditorCatalog } from './useMapEditorCatalog';

/**
 * Whether this map has the map editor at all.
 *
 * The room kit is the gate. A map's catalog is emitted by its own build (the MytaMapEditor GameCast
 * metadata provider, under `plugins.mapEditor`) and projected onto the map record, so a build that
 * ships no kit has no rooms to offer and no geometry to spawn — `useMapEditorCatalog` reports that
 * as `source: 'fallback'`, with no elements at all.
 *
 * Gating on the kit rather than on a setting makes this self-maintaining: a map gains the map editor
 * by shipping one, and there is no second place to keep in step with what the build actually
 * contains. Before this, every surface was ungated or defaulted on, so the Layout panel appeared on
 * every map offering the test map's galleries and corridors — rooms that map cannot build.
 *
 * The SPAWN_LAYOUT flag still applies on top, which is the job it is good at: switching the feature
 * off for a map that does ship a kit. It is deliberately not the gate on its own —
 * `isMapFeatureFlagEnabled` fails open, and it is shared with the spot and trigger teleport actions,
 * so making it fail closed would take those down with it.
 *
 * @returns True when every map-editor surface should render; false hides them outright — no
 *          placeholder and no empty state, because a map without a kit has nothing to explain.
 */
export const useCanUseMapEditor = (): boolean => {
  const { currentRoom, currentEvent } = useMytaverseEvent();
  const { source } = useMapEditorCatalog();

  return (
    source === 'build' &&
    isMapFeatureFlagEnabled(currentEvent, currentRoom?.map, 'SPAWN_LAYOUT')
  );
};
